import {
    Component,
    computed,
    inject,
    Input,
    signal,
    ViewChild,
} from '@angular/core';
import { Button, ButtonDirective } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule, Location, NgIf } from '@angular/common';
import { ConfirmationService, PrimeTemplate } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SolicitudEvalDocTipoInterface } from 'src/app/officials/evaluaciones/interfaces/evaluaciones.interface';
import {
    ArchivoItem,
    ProyectoItem,
} from 'src/app/users/interfaces/proyecto.interface';
import { TramiteService } from 'src/app/users/tramites/services/tramite.service';
import { DescargarPlantillaComponent } from '../../components/descargar-plantilla/descargar-plantilla.component';
import { SubirResolucionComponent } from '../../components/subir-resolucion/subir-resolucion.component';
import Swal from 'sweetalert2';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { ProyectoService } from 'src/app/users/proyectos/services/proyecto.service';
import { ObtencionGenerica } from '../../../interface/ver-proyecto.interface';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { environment } from 'src/environments/environment.development';
import CONSTANTS from 'src/utils/resources/constants';
import { RecorteFotoComponent } from '../../components/recorte-foto/recorte-foto.component';
import { RequisitoService } from 'src/app/users/mnto-requisitos/services/requisito.service';
import { log } from 'logrocket';

@Component({
    selector: 'app-resoluciones',
    standalone: true,
    imports: [
        ButtonDirective,
        TooltipModule,
        Button,
        PrimeTemplate,
        TableModule,
        TagModule,
        CommonModule,
        DescargarPlantillaComponent,
        SubirResolucionComponent,
        ConfirmPopupModule,
        ProgressSpinnerModule,
        RecorteFotoComponent,
    ],
    templateUrl: './resoluciones.component.html',
    styleUrl: './resoluciones.component.scss',
})
export class ResolucionesComponent {
    protected tramiteService = inject(TramiteService);
    private confirmationService = inject(ConfirmationService);
    cols = [];
    currentAction = signal<'upload' | 'dowload' | null>(null);
    showAction = computed(() => this.currentAction() !== null);

    resolutionDialogConfig = signal({
        open: false,
        isUploadResolution: false,
    });
    openDialogResolucion: boolean = false;
    solicitudResoluciones: SolicitudEvalDocTipoInterface[] = [];

    resolucionesFilter(table: Table, event: Event) {
        table.filterGlobal(
            (event.target as HTMLInputElement).value,
            'contains'
        );
    }

    tagStyle(estado: boolean) {
        switch (estado) {
            case true:
                return { background: '#28a745' };
            case false:
                return { background: '#FF0000FF' };
            default:
                return { background: '#6c757d' };
        }
    }

    openDialog(isUploadResolution: boolean) {
        this.resolutionDialogConfig.set({
            open: true,
            isUploadResolution: isUploadResolution,
        });
    }

    closeDialogResolucion(event: boolean) {
        this.openDialogResolucion = event;
        this.openDialogResolucion = false;

        this.resolutionDialogConfig.set({
            open: false,
            isUploadResolution: false,
        });
    }

    setAction(action: 'upload' | 'dowload' | null): void {
        this.currentAction.set(action);
    }

    @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;

    private aprobarSolicitud() {
        this.tramiteService
            .aprobarSolicitud(this.selected?.idProyecto!)
            .subscribe({
                next: () => {
                    Swal.fire(
                        'Éxito',
                        'Solicitud aprobada correctamente',
                        'success'
                    );
                    this.is_solicitudAprobada = true;
                },
                error: (err) => {
                    Swal.fire(
                        'Error',
                        'No se pudo aprobar la solicitud',
                        'error'
                    );
                    console.error(err);
                },
            });
    }

    accept() {
        this.confirmPopup.accept();
    }

    reject() {
        this.confirmPopup.reject();
    }

    confirm(event: Event) {
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: 'Seguro deseas aprobar la solicitud?',
            accept: () => {
                this.aprobarSolicitud();
            },
            reject: () => {},
        });
    }

    /*===========================================================================================*/
    /*VARIABLES ---------------------------------------------------------------------------------*/
    /*===========================================================================================*/

    //PRIVATES ------------------------------------------------------------------------------------

    static aprobada: string = 'Aprobada';

    //PRIVATES ------------------------------------------------------------------------------------
    /** Usualmente definidos para recibir los datos de los inputs */

    private consoleEnabled: boolean = CONSTANTS.consoleEnabled; //  environment.showConsole; // Cambiar a false para desactivar los logs de consola
    private _selected: ProyectoItem | null = null;
    private _registro: ObtencionGenerica = {};

    //PUBLIC --------------------------------------------------------------------------------------
    /** Usualmente definidos para ser utilizados en el html */

    public carnetData: ObtencionGenerica = {};
    public is_solicitudAprobada: boolean = false;
    public is_rp: boolean = false;

    /*===========================================================================================*/
    /*INPUTS & OUTPUTS --------------------------------------------------------------------------*/
    /*===========================================================================================*/

    //INPUTS --------------------------------------------------------------------------------------

    @Input()
    set selected(data: ProyectoItem | null) {
        this._selected = data;
        if (data) {
            //onChange detected
        }
    }

    get selected(): ProyectoItem | null {
        return this._selected;
    }

    //-------------------------------------------

    @Input()
    set registro(data: ObtencionGenerica) {
        this._registro = data;
        if (data) {
            //onChange detected
            if (this.consoleEnabled)
                console.log('IS RP:', this.RP(this.selected));

            this.is_solicitudAprobada = this.isSolicitudAprovada();
            this.is_rp = this.RP(this.selected);
        }
    }

    get registro(): ObtencionGenerica {
        return this._registro;
    }

    //OUTPUTS --------------------------------------------------------------------------------------

    /*===========================================================================================*/
    /*CONSTRUCTOR -------------------------------------------------------------------------------*/
    /*===========================================================================================*/

    constructor(
        private proyectoServices: ProyectoService,
        private requisitoService: RequisitoService
    ) {}

    /*===========================================================================================*/
    /*METHODS -----------------------------------------------------------------------------------*/
    /*===========================================================================================*/

    /**
     * Método para cargar información del carnet asociado a la solicitud seleccionada. En este caso
     * estamos utilizando la informacion del proyecto seleccionado, el cual es obtenido mediante el
     * input [selected]
     */
    async cargarCarnet(proyecto?: ProyectoItem | null): Promise<void> {
        //no hacer nada si no hay proyecto
        if (!proyecto) {
            return;
        }

        //Obtener el id de la solicitud correspondiente
        const id_Solicitud = proyecto?.idSolicitud;
        proyecto.carnetLoading = true;

        console.log('Estado de la solicitud', proyecto.estadoSolicitud);

        if (this.consoleEnabled) {
            console.log(
                `ID de solicitud en archivo {lista-proyectos-components.ts}: ${id_Solicitud}`
            );
        }

        if (!id_Solicitud) {
            if (this.consoleEnabled) {
                console.warn('ID de solicitud no disponible aún');
            }
            return;
        }

        try {
            proyecto.carnetGenerado = false;

            //comsumir API de respuesta para la generacion del carnet
            //antes de consumir el endpoint que genera el PDF
            this.proyectoServices
                .getCarnetByIdSolicitud(id_Solicitud)
                .subscribe((response) => {
                    if (response.success) {
                        //Mostrar datos de la respuesta en caso de exito
                        if (this.consoleEnabled) {
                            console.log(
                                'Informacion: ',
                                response.data?.['data']
                            );
                        }

                        //cargamos con los datos en caso de necesitarse en el futuro
                        this.carnetData = response.data?.['data'];

                        //Habilitamos la activacion del endpoint de generacion del PDF del carnet
                        proyecto.carnetGenerado = true;
                        proyecto.carnetLoading = false;
                        this.mensajeSuccess(
                            'Generado con exito',
                            'Su carnet se ha generado, ya lo puede descargar'
                        );
                    } else {
                        //Hinnabilitamos la carga del PDF del carnet
                        proyecto.carnetGenerado = false;
                        proyecto.carnetLoading = false;
                        this.mensajeWarning(
                            'Lo lamentamos',
                            'Su carnet no fue posible generarse'
                        );
                    }
                });
        } catch (error) {
            proyecto.carnetLoading = false;
            proyecto.carnetGenerado = false;
            if (this.consoleEnabled) {
                console.error('Error al cargar datos del carnet:', error);
            }
            this.mensajeWarning(
                'Lo lamentamos',
                'Su carnet no fue posible generarse, intente mas tarde'
            );
        }
    }

    recorteFotoVisible = false;
    fotoParaRecortar: string | undefined = undefined;

    async recortarFotoCarnetPDF(proyecto: ProyectoItem): Promise<void> {
        if (!proyecto) return;

        proyecto.carnetLoading = true;
        proyecto.carnetDescargado = false;

        console.log('proyecto seleccionado:', proyecto);

        // 1. Obtener los tipos de requisitos
        this.requisitoService.getRequisitos('1').subscribe({
            next: (tiposRequisito) => {
                // Por ejemplo, buscar el tipo de requisito para foto
                // Ajusta el filtro según el nombre o id que corresponda a "foto"

                console.log('Tipos de requisitos obtenidos:', tiposRequisito);

                const tipoFoto = tiposRequisito.find((t: any) => {
                    const nombre = t.nombreRead?.toLowerCase() ?? '';
                    return nombre.includes('rp_foto');
                });

                const tipoLogo = tiposRequisito.find((t: any) => {
                    const nombre = t.nombreRead?.toLowerCase() ?? '';
                    return nombre.includes('rp_logo');
                });

                console.log('Tipo de foto encontrado:', tipoFoto);
                const idRequisitoFoto = tipoFoto?.idRequisito;
                const idRequisitoLogo = tipoLogo?.idRequisito;

                if (!idRequisitoFoto) {
                    this.mensajeWarning(
                        'Error',
                        'No se encontró el tipo de requisito para foto o logo.'
                    );
                    proyecto.carnetLoading = false;
                    return;
                }

                console.log('ID de requisito de foto:', idRequisitoFoto);

                // 2. Obtener los archivos del proyecto
                this.proyectoServices
                    .getArchivosProyecto(proyecto.idProyecto)
                    .subscribe({
                        next: async (result) => {
                            let archivos: any[] = result.body ?? [];

                            // 4. Buscar el archivo de foto o logo dentro de los archivos filtrados
                            const archivoFoto = archivos.find(
                                (a) =>
                                    a.idRequisito == idRequisitoFoto ||
                                    a.idRequisito == idRequisitoLogo
                            );

                            // if (!archivoFoto || !archivoFoto.hash) {
                            //     this.mensajeWarning(
                            //         'Error',
                            //         'No se encontró la foto o logo en los archivos del proyecto.'
                            //     );
                            //     proyecto.carnetLoading = false;
                            //     return;
                            // }

                            // 5. Descargar el archivo como blob
                            try {
                                const response = await fetch(
                                    `${environment.apiVucUrl}api/v1.0/archivos/${archivoFoto.hash}`,
                                    { method: 'GET' }
                                );
                                const blob = await response.blob();
                                const reader = new FileReader();
                                reader.onload = () => {
                                    this.fotoParaRecortar = reader.result as
                                        | string
                                        | undefined;
                                    this.recorteFotoVisible = true;
                                };
                                reader.readAsDataURL(blob);
                            } catch (error) {
                                this.mensajeWarning(
                                    'Error',
                                    'No se pudo obtener la foto.'
                                );
                                proyecto.carnetLoading = false;
                            }
                        },
                        error: (error) => {
                            this.mensajeWarning(
                                'Error',
                                'No se pudieron obtener los archivos del proyecto.'
                            );
                            proyecto.carnetLoading = false;
                        },
                    });
            },
            error: (error) => {
                this.mensajeWarning(
                    'Error',
                    'No se pudieron obtener los tipos de requisitos.'
                );
                proyecto.carnetLoading = false;
            },
        });
    }

    onFotoRecortada(base64: any) {
        this.recorteFotoVisible = false;

        if (this.selected && base64) {
            this.comprimirBase64(base64, 0.7).then((base64Comprimido) => {
                this.descargarCarnetPDF(this.selected!, base64Comprimido);
            });
        }
    }

    onRecorteFotoCerrado() {
        if (this.selected) {
            this.selected.carnetLoading = false;
        }
    }

    // Método utilitario para comprimir base64 usando canvas
    comprimirBase64(base64: string, calidad: number = 0.7): Promise<string> {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    // Exporta como JPEG comprimido
                    const base64Comprimido = canvas.toDataURL(
                        'image/jpeg',
                        calidad
                    );
                    resolve(base64Comprimido);
                } else {
                    // Si falla, retorna el original
                    resolve(base64);
                }
            };
            img.onerror = () => resolve(base64);
            img.src = base64;
        });
    }

    async descargarCarnetPDF(
        proyecto: ProyectoItem,
        cropData: any
    ): Promise<void> {
        if (!proyecto) return;

        const id_proyecto = proyecto.idProyecto;
        proyecto.carnetLoading = true;
        proyecto.carnetDescargado = false;

        if (!id_proyecto) {
            if (this.consoleEnabled) {
                console.warn('ID de proyecto no disponible aún');
            }
            return;
        }

        try {
            this.proyectoServices
                .generarCarnetPdf(id_proyecto, cropData)
                .subscribe({
                    next: (pdfResponse: any) => {
                        if ('success' in pdfResponse && !pdfResponse.success) {
                            proyecto.carnetDescargado = false;
                            proyecto.carnetLoading = false;
                            this.mensajeWarning(
                                'Lo lamentamos',
                                'No fue posible descargar su carnet PDF'
                            );
                            return;
                        }

                        // Cambia aquí: usa el nombre real
                        const blob = pdfResponse.blob || pdfResponse;
                        const fileName =
                            pdfResponse.fileName ||
                            `carnet_proyecto_${id_proyecto}.pdf`;

                        const downloadURL = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadURL;
                        link.download = fileName;
                        link.click();
                        window.URL.revokeObjectURL(downloadURL);

                        this.mensajeSuccess(
                            'Generado con exito',
                            'El carnet se ha generado y descargado correctamente.'
                        );

                        proyecto.carnetDescargado = true;
                        proyecto.carnetLoading = false;
                    },
                    error: (err) => {
                        console.error('Error al generar el PDF:', err);
                        this.mensajeWarning(
                            'Lo lamentamos',
                            'No fue posible descargar su carnet PDF'
                        );
                        proyecto.carnetDescargado = false;
                        proyecto.carnetLoading = false;
                    },
                });
        } catch (error) {
            proyecto.carnetDescargado = false;
            proyecto.carnetLoading = false;
            if (this.consoleEnabled) {
                console.error('Error al cargar datos del carnet PDF:', error);
            }
            this.mensajeWarning(
                'Lo lamentamos',
                'No fue posible descargar su carnet PDF. Intente mas tarde'
            );
        }
    }

    /*===========================================================================================*/
    /*UTILS -------------------------------------------------------------------------------------*/
    /*===========================================================================================*/

    mensajeWarning(title?: string, mensaje?: string): void {
        Swal.fire({
            title: title ? title : '...',
            text: mensaje ? mensaje : '...',
            icon: 'warning',
        });
    }

    mensajeSuccess(title?: string, mensaje?: string): void {
        Swal.fire({
            title: title ? title : '...',
            text: mensaje ? mensaje : '...',
            icon: 'success',
        });
    }

    isRegistroProfesional(proyecto?: ProyectoItem | null): boolean {
        //no hacer nada si no hay proyecto
        if (!proyecto) {
            return false;
        }

        return proyecto.nombre.startsWith('RP_') || false;
    }

    isSolicitudAprovada(): boolean {
        // console.log('issolicitu dAprobada : ', (this.registro?.['estado']=== ResolucionesComponent.aprobada) );

        console.log('this.is_solicitudAprobada : ', this.is_solicitudAprobada);

        return this.registro?.['estado'] === ResolucionesComponent.aprobada;
    }

    /* validando si posee trámites pendientes y el trámite es RP_ */
    RP(tramite: any): boolean {
        return tramite?.nombre?.startsWith('RP_') === true;
    }
}
